import classNames from 'classnames';
import type { MouseEventHandler, ReactNode } from 'react';

import { css } from '@sorare/css';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { ConditionalWrapper } from '#src/atoms/layout/ConditionalWrapper/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';

const root = css`
  width: 100%;
  min-width: 120px;
  max-width: max-content;
  margin: 0 auto;
  align-items: stretch;
  background: var(--c-surface-background);
  border-radius: var(--unit);
  gap: 1px;

  --footer-bg-color: var(--c-nd-100);
  &:hover {
    --footer-bg-color: var(--c-nd-150);
  }

  & > :last-child {
    border-bottom-left-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;
const top = css`
  display: grid;
  grid-template-columns: max-content var(--quadruple-unit) max-content;

  &.centered {
    grid-template-columns: minmax(0, 1fr) var(--quadruple-unit) minmax(0, 1fr);
  }
  grid-template-rows: var(--triple-unit);
  background-color: var(--footer-bg-color);
  border-top-left-radius: var(--unit);
  border-top-right-radius: var(--unit);
  padding: 0 var(--half-unit);
  justify-content: center;

  transition: background-color 0.3s ease-in-out;
`;
const clickableAreaStyle = css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1px;
  width: 100%;
  text-align: initial;

  &:last-child > :last-child {
    border-bottom-left-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;
const matchupsClass = css`
  height: var(--double-and-a-half-unit);
  background-color: var(--footer-bg-color);

  transition: background-color 0.3s ease-in-out;

  &:last-child {
    overflow: hidden;
  }
`;

type Props = {
  children: ReactNode;
  left: ReactNode;
  right: ReactNode;
  matchups?: ReactNode;
  gameOdds?: ReactNode;
  gameDate?: ReactNode;
  extra?: ReactNode;
  centerChildren?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

export const CardFooter = ({
  children,
  left,
  right,
  matchups,
  gameOdds,
  gameDate,
  extra,
  centerChildren,
  onClick,
}: Props) => {
  return (
    <Vertical center gap={0}>
      <Vertical className={root} gap={0}>
        <ConditionalWrapper
          Wrapper={ButtonBase}
          wrap={!!onClick}
          props={{ onClick, className: clickableAreaStyle }}
        >
          <Horizontal
            gap={0.5}
            className={classNames(top, { centered: centerChildren })}
          >
            <Horizontal center>{left}</Horizontal>
            <div className="mt-0.5">{children}</div>
            <Horizontal gap={0.5} center>
              {right}
            </Horizontal>
          </Horizontal>
          {!!matchups && (
            <Horizontal className={matchupsClass} gap={0}>
              {matchups}
            </Horizontal>
          )}
        </ConditionalWrapper>
        {gameOdds}
        {extra}
      </Vertical>
      <div className="py-0.5">{gameDate}</div>
    </Vertical>
  );
};
